import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import CommonLink from 'components_v2/CommonLink'
import { Box, Flex, P, Tag } from 'ui/bend'
import { Image as ImageAcro } from 'ui/acro'

import {
  formatEventBadge,
  formatEventDate,
  formatEventLocation,
  formatEventTimeRange
} from './formatEvent'

const PLACEHOLDER_IMAGE =
  'https://alomoves.s3.amazonaws.com/manual_uploads/shared/search/placeholder-cards/rectangle-card.jpg'

const STATUS_LABELS = {
  registered: 'Registered',
  waitlisted: 'Waitlisted',
  pending: 'Pending'
}

const CardLink = styled(CommonLink)(
  css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    color: 'inherit',
    textDecoration: 'none'
  })
)

const ImageWrap = styled(Box)(
  css({
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 10',
    overflow: 'hidden',
    mb: '12px',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    }
  })
)

const DateBadge = styled(Flex)(
  css({
    position: 'absolute',
    top: '12px',
    left: '12px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: 'base',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
  })
)

const BadgeMonth = styled(P)(
  css({
    m: 0,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
    color: 'grey4'
  })
)

const BadgeDay = styled(P)(
  css({
    m: 0,
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.1,
    color: 'primary'
  })
)

const Title = styled(P)(
  css({
    m: 0,
    color: 'primary',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '135%',
    mb: '8px'
  })
)

const Meta = styled(P)(
  css({
    m: 0,
    color: 'grey4',
    fontSize: '13px',
    lineHeight: '150%'
  })
)

const MyEventCarouselCard = forwardRef(
  (
    {
      carouselItem,
      htmlId,
      id,
      title,
      coverUrl,
      startAt,
      endAt,
      timezone,
      registrationStatus,
      lumaUrl,
      isPast,
      city,
      region,
      venue,
      locationType
    },
    ref
  ) => {
    const dateBadge = formatEventBadge(startAt, timezone)
    const dateLabel = formatEventDate(startAt, timezone)
    const timeLabel = formatEventTimeRange(startAt, endAt, timezone)
    const locationLabel = formatEventLocation({
      city,
      region,
      venue,
      locationType
    })
    const detailPath = id ? `/events/${id}` : '#'
    const href = detailPath

    return (
      <Flex
        id={htmlId}
        ref={ref}
        flexDirection='column'
        height='100%'
        className={carouselItem ? 'swiper-slide unstyled' : 'unstyled'}
      >
        <CardLink to={href} className='unstyled'>
          <ImageWrap>
            <ImageAcro
              src={coverUrl || PLACEHOLDER_IMAGE}
              alt={title}
              objectFit='cover'
              onError={(elem) => {
                elem.currentTarget.src = PLACEHOLDER_IMAGE
              }}
            />
            {dateBadge && (
              <DateBadge aria-hidden='true'>
                <BadgeMonth>{dateBadge.month}</BadgeMonth>
                <BadgeDay>{dateBadge.day}</BadgeDay>
              </DateBadge>
            )}
          </ImageWrap>

          <Flex alignItems='center' gap='8px' mb='8px'>
            {isPast ? (
              <Tag>Past</Tag>
            ) : (
              registrationStatus &&
              registrationStatus !== 'none' && (
                <Tag>{STATUS_LABELS[registrationStatus] || 'Registered'}</Tag>
              )
            )}
          </Flex>

          <Title>{title}</Title>
          {dateLabel && <Meta>{dateLabel}</Meta>}
          {timeLabel && <Meta>{timeLabel}</Meta>}
          {locationLabel && <Meta>{locationLabel}</Meta>}
        </CardLink>
      </Flex>
    )
  }
)

MyEventCarouselCard.displayName = 'MyEventCarouselCard'

MyEventCarouselCard.propTypes = {
  carouselItem: PropTypes.bool,
  htmlId: PropTypes.string,
  id: PropTypes.string,
  title: PropTypes.string,
  coverUrl: PropTypes.string,
  startAt: PropTypes.string,
  endAt: PropTypes.string,
  timezone: PropTypes.string,
  registrationStatus: PropTypes.string,
  isPast: PropTypes.bool,
  lumaUrl: PropTypes.string,
  city: PropTypes.string,
  region: PropTypes.string,
  venue: PropTypes.string,
  locationType: PropTypes.string
}

export default MyEventCarouselCard
